// v1.12.0 · CRM · Fase 6: Reportes (ventas, citas, cobranza, equipo, leads), auditoría y accesos. Solo administradores.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Download, Search } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { api, errMsg, fdatetime, today, inputCls, btn, downloadBlob, Empty } from './ui';

const SUBS = [['ventas', 'Ventas'], ['citas', 'Citas'], ['cobranza', 'Cobranza'], ['equipo', 'Equipo'], ['leads', 'Leads'], ['audit', 'Auditoría'], ['access', 'Accesos']];
const money = (n) => `$${Number(n || 0).toLocaleString('es-EC', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (n) => Number(n || 0).toLocaleString('es-EC', { maximumFractionDigits: 1 });
const addDays = (ymd, n) => { const d = new Date(`${ymd}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

// KPIs por reporte: [clave, etiqueta, formato]
const KPIS = {
  ventas: [['created', 'Oportunidades creadas'], ['won', 'Ganadas'], ['won_value', 'Valor ganado', 'm'], ['win_rate', '% de cierre', '%'], ['avg_deal', 'Ticket promedio', 'm'], ['avg_days_to_close', 'Días para cerrar']],
  citas: [['total', 'Citas'], ['showed', 'Asistieron'], ['noshow', 'No asistieron'], ['attendance_rate', '% asistencia', '%'], ['web_bookings', 'Reservas web'], ['cancelled', 'Canceladas']],
  cobranza: [['invoiced', 'Facturado', 'm'], ['collected', 'Cobrado', 'm'], ['collection_rate', '% cobrado', '%'], ['outstanding', 'Por cobrar hoy', 'm'], ['overdue', 'Vencido hoy', 'm'], ['overdue_share', '% vencido', '%']],
  equipo: [['people', 'Personas'], ['messages', 'Mensajes enviados'], ['tasks', 'Tareas completadas'], ['won_value', 'Valor ganado', 'm']],
  leads: [['new_contacts', 'Contactos nuevos'], ['web_leads', 'Leads web'], ['answered', 'Respondidos'], ['unanswered', 'Sin respuesta'], ['median_first_response_min', 'Mediana 1.ª respuesta (min)'], ['within_1h_pct', '% en menos de 1 h', '%']],
};
const SERIES = {
  ventas: [['won_value', 'Valor ganado', '#2E7A22', 'm']], citas: [['total', 'Citas', '#2a78d6']],
  cobranza: [['invoiced', 'Facturado', '#2F3D8E', 'm'], ['collected', 'Cobrado', '#2E7A22', 'm']], leads: [['total', 'Contactos nuevos', '#2a78d6']],
};
const fmt = (v, f) => (f === 'm' ? money(v) : f === '%' ? `${num(v)} %` : num(v));

function Period({ from, to, onChange }) {
  const t = today();
  const presets = [['30 días', addDays(t, -29), t], ['90 días', addDays(t, -89), t], ['Este año', `${t.slice(0, 4)}-01-01`, t], ['12 meses', addDays(t, -364), t]];
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {presets.map(([l, a, b]) => <button key={l} onClick={() => onChange(a, b)} className={`rounded-full px-3 py-1 text-xs font-semibold ${from === a && to === b ? 'bg-[#2F3D8E] text-white' : 'border border-gray-300 bg-white text-gray-700'}`}>{l}</button>)}
      <input type="date" className="rounded-lg border border-gray-300 px-2 py-1 text-xs" value={from} max={to} onChange={(e) => onChange(e.target.value, to)} aria-label="Desde" />
      <span className="text-xs text-gray-500">a</span>
      <input type="date" className="rounded-lg border border-gray-300 px-2 py-1 text-xs" value={to} min={from} onChange={(e) => onChange(from, e.target.value)} aria-label="Hasta" />
    </div>
  );
}

function Table({ t, onExport }) {
  const isMoneyCol = (c) => /valor|monto|saldo|facturado|cobrado/i.test(c);
  return (
    <section className="min-w-0 rounded-xl border border-gray-200 bg-white" data-testid="report-table">
      <div className="flex items-center justify-between gap-2 border-b px-4 py-2.5">
        <h3 className="text-[14px] font-bold text-[#0F2A3A]">{t.title}</h3>
        <button className="inline-flex items-center gap-1 text-xs font-semibold text-[#2F3D8E] hover:underline" onClick={onExport}><Download size={13} /> CSV</button>
      </div>
      {t.rows.length === 0 ? <p className="px-4 py-6 text-center text-sm text-gray-500">Sin datos en el periodo.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500"><tr>{t.columns.map((c, i) => <th key={c} className={`whitespace-nowrap px-3 py-2 font-semibold ${i ? 'text-right' : ''}`}>{c}</th>)}</tr></thead>
            <tbody>{t.rows.map((r, k) => (
              <tr key={k} className="border-t">{r.map((v, i) => (
                <td key={i} className={`whitespace-nowrap px-3 py-1.5 ${i ? 'text-right tabular-nums' : 'font-medium'}`}>{typeof v === 'number' ? (isMoneyCol(t.columns[i]) ? money(v) : /%/.test(t.columns[i]) ? `${num(v)} %` : num(v)) : v}</td>
              ))}</tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Report({ name, from, to }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let alive = true;
    setD(null);
    api.get(`/reports/${name}`, { date_from: from, date_to: to }).then((r) => { if (alive) setD(r); }).catch((e) => { toast.error(errMsg(e)); if (alive) setD({ error: true }); });
    return () => { alive = false; };
  }, [name, from, to]);
  const exportCsv = async (key) => {
    try { downloadBlob(await api.blob(`/reports/${name}`, { date_from: from, date_to: to, format: 'csv', table: key }), `reporte_${name}_${key}_${from}_${to}.csv`); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <p className="py-12 text-center text-sm text-gray-500">Calculando…</p>;
  if (d.error) return <Empty title="No se pudo cargar el reporte">Intente con otro periodo.</Empty>;
  const series = SERIES[name];
  const months = d.series?.months || [];
  return (
    <div className="space-y-4" data-testid={`report-${name}`}>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        {KPIS[name].map(([k, l, f]) => (
          <div key={k} className="min-w-0 rounded-xl border border-gray-200 bg-white px-4 py-3" data-testid="report-kpi">
            <div className="truncate text-[20px] font-extrabold text-[#0F2A3A]">{fmt(d.kpis[k], f)}</div>
            <div className="text-xs text-gray-500">{l}</div>
          </div>
        ))}
      </div>
      {series && months.length > 0 && (
        <section className="rounded-xl border border-gray-200 bg-white p-4">
          <h3 className="mb-2 text-[14px] font-bold text-[#0F2A3A]">Por mes</h3>
          <div className="h-[220px]">
            <ResponsiveContainer>
              <BarChart data={months} margin={{ left: 0, right: 8, top: 6 }}>
                <CartesianGrid vertical={false} stroke="#EEF0F3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} width={56} tickFormatter={(v) => (series[0][3] === 'm' ? `$${v >= 1000 ? `${Math.round(v / 100) / 10}K` : v}` : v)} />
                <Tooltip formatter={(v, n) => [series.find((s) => s[1] === n)?.[3] === 'm' ? money(v) : num(v), n]} cursor={{ fill: '#F3F4F6' }} />
                {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
                {series.map(([k, l, c]) => <Bar key={k} dataKey={k} name={l} fill={c} radius={[4, 4, 0, 0]} maxBarSize={36} />)}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {Object.entries(d.tables).map(([k, t]) => <Table key={k} t={t} onExport={() => exportCsv(k)} />)}
      </div>
      {name === 'leads' && <p className="text-xs text-gray-500">Primera respuesta: desde el primer mensaje del formulario o la reserva web hasta el primer correo, WhatsApp, SMS o llamada de una persona del equipo (no cuentan las notas ni los correos automáticos).</p>}
      {name === 'cobranza' && <p className="text-xs text-gray-500">«Por cobrar» y la antigüedad de saldos se calculan a hoy, sin importar el periodo elegido.</p>}
    </div>
  );
}

function Audit() {
  const [f, setF] = useState({ user_name: '', q: '', date_from: '', date_to: '' });
  const [qInput, setQInput] = useState('');
  const [page, setPage] = useState(1);
  const [d, setD] = useState({ items: [], total: 0, page_size: 50, users: [] });
  useEffect(() => { const t = setTimeout(() => { setF((x) => ({ ...x, q: qInput })); setPage(1); }, 300); return () => clearTimeout(t); }, [qInput]);
  const load = useCallback(async () => { try { setD(await api.get('/audit', { ...f, page })); } catch (e) { toast.error(errMsg(e)); } }, [f, page]);
  useEffect(() => { load(); }, [load]);
  const exportCsv = async () => { try { downloadBlob(await api.blob('/audit/export', f), `auditoria_crm_${today()}.csv`); } catch (e) { toast.error(errMsg(e)); } };
  const pages = Math.max(1, Math.ceil(d.total / d.page_size));
  return (
    <div className="space-y-3" data-testid="crm-audit">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className={`${inputCls} pl-8`} placeholder="Buscar acción, ruta o id" value={qInput} onChange={(e) => setQInput(e.target.value)} />
        </div>
        <select className="rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm" value={f.user_name} onChange={(e) => { setF({ ...f, user_name: e.target.value }); setPage(1); }} aria-label="Usuario">
          <option value="">Todos los usuarios</option>{d.users.map((u) => <option key={u} value={u}>{u}</option>)}
        </select>
        <input type="date" className="rounded-lg border border-gray-300 px-2 py-2 text-sm" value={f.date_from} onChange={(e) => { setF({ ...f, date_from: e.target.value }); setPage(1); }} aria-label="Desde" />
        <input type="date" className="rounded-lg border border-gray-300 px-2 py-2 text-sm" value={f.date_to} onChange={(e) => { setF({ ...f, date_to: e.target.value }); setPage(1); }} aria-label="Hasta" />
        <button className={btn.ghost} onClick={exportCsv}><Download size={14} /> CSV</button>
      </div>
      {d.items.length === 0 ? <Empty title="Sin registros">Aquí aparece cada cambio hecho en el CRM: quién, qué y cuándo.</Empty> : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500"><tr>{['Fecha', 'Usuario', 'Acción', 'Id', 'IP'].map((h) => <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>)}</tr></thead>
            <tbody>{d.items.map((i) => (
              <tr key={i.id} className="border-t">
                <td className="whitespace-nowrap px-3 py-1.5 text-gray-600">{fdatetime(i.at)}</td>
                <td className="px-3 py-1.5 font-medium">{i.user || '—'}{i.role ? <span className="ml-1 text-xs text-gray-400">{i.role}</span> : null}</td>
                <td className="px-3 py-1.5">{i.action}</td>
                {/* v1.12.1 · número del documento creado (COT-/FAC-) cuando lo hay, y el id que se creó al convertir o duplicar */}
                <td className="max-w-[180px] truncate px-3 py-1.5 font-mono text-[11px] text-gray-500" title={[i.entity_id, i.created_id && i.created_id !== i.entity_id ? `creado: ${i.created_id}` : ''].filter(Boolean).join(' · ')}>
                  {i.ref ? <span className="font-sans text-xs font-semibold text-gray-700">{i.ref}</span> : (i.entity_id || '—')}
                  {i.created_id && i.created_id !== i.entity_id && !i.ref ? <span className="block">→ {i.created_id}</span> : null}
                </td>
                <td className="px-3 py-1.5 text-xs text-gray-500">{i.ip || '—'}
                  {i.ip_reported ? <span className="block text-[11px] text-amber-700" title="Lo que declaró el navegador en el encabezado X-Forwarded-For; no es confiable">declarada: {i.ip_reported}</span> : null}</td>
              </tr>))}</tbody>
          </table>
          <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-gray-600">
            <span>{d.total} registros</span>
            <div className="flex items-center gap-2"><button className={btn.ghost} disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</button><span>Página {page} de {pages}</span><button className={btn.ghost} disabled={page >= pages} onClick={() => setPage(page + 1)}>Siguiente</button></div>
          </div>
        </div>
      )}
      <p className="text-xs text-gray-500">Se registran los cambios (crear, editar, borrar, enviar) del CRM, las reservas y documentos públicos y el formulario web. No se guarda el contenido de lo enviado.</p>
    </div>
  );
}

function Access() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.get('/access').then((r) => setItems(r.items)).catch((e) => { toast.error(errMsg(e)); setItems([]); }); }, []);
  if (!items) return <p className="py-10 text-center text-sm text-gray-500">Cargando…</p>;
  return (
    <div className="space-y-3" data-testid="crm-access">
      <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
        <table className="w-full min-w-[620px] text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500"><tr>{['Persona', 'Usuario', 'Rol', 'Entra por', 'Nivel'].map((h) => <th key={h} className="px-3 py-2.5 font-semibold">{h}</th>)}</tr></thead>
          <tbody>{items.map((u) => (
            <tr key={u.id} className="border-t">
              <td className="px-3 py-2 font-medium">{u.name}</td><td className="px-3 py-2 text-gray-600">{u.username}</td><td className="px-3 py-2">{u.role}</td><td className="px-3 py-2">{u.via}</td>{/* v1.12.1 · de dónde viene la entrada y, abajo del nivel, de dónde viene el nivel */}
              <td className="px-3 py-2">{u.admin ? <span className="rounded-full bg-[#2F3D8E]/10 px-2 py-0.5 text-xs font-semibold text-[#2F3D8E]">Administrador</span>
                : u.own_only ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800">Solo sus registros</span>
                  : <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">Todo el CRM</span>}
                <span className="mt-0.5 block text-[11px] text-gray-500">por {u.level_via}</span>
                {u.note ? <span className="block text-[11px] text-amber-700">{u.note}</span> : null}</td>
            </tr>))}</tbody>
        </table>
      </div>
      <div className="rounded-xl border border-gray-200 bg-white p-4 text-sm text-gray-700">
        <p className="font-semibold text-[#0F2A3A]">Cómo cambiar los permisos</p>
        <ul className="mt-1 list-disc space-y-1 pl-5">
          <li>En <a href="/usuarios" className="font-semibold text-[#2F3D8E] underline">Usuarios</a>, edite a la persona y marque en módulos adicionales:</li>
          <li><b>CRM: contactos y oportunidades</b> — da acceso al CRM a roles que no lo tienen.</li>
          <li><b>CRM: administrador</b> — configuración, borrados, reportes, auditoría y automatizaciones.</li>
          <li><b>CRM: solo sus propios registros</b> — ve únicamente sus contactos, oportunidades, conversaciones, tareas, citas y documentos; lo que crea queda a su nombre y no puede reasignar.</li>
          <li>Los cambios aplican cuando la persona vuelve a iniciar sesión.</li>
        </ul>
      </div>
    </div>
  );
}

export default function CrmReports({ meta }) {
  const [sub, setSub] = useState('ventas');
  const t = today();
  const [period, setPeriod] = useState([addDays(t, -89), t]);
  const isReport = useMemo(() => !['audit', 'access'].includes(sub), [sub]);
  if (!meta.is_admin) return <p className="rounded-xl border bg-white p-6 text-sm text-gray-600">Los reportes y la auditoría son para administradores del CRM.</p>;
  return (
    <div className="space-y-3" data-testid="crm-reports">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {SUBS.map(([k, l]) => <button key={k} onClick={() => setSub(k)} className={`rounded-lg px-3 py-1.5 text-sm font-semibold ${sub === k ? 'bg-[#0F2A3A] text-white' : 'border border-gray-300 bg-white text-gray-700'}`}>{l}</button>)}
        </div>
        {isReport && <div className="ml-auto"><Period from={period[0]} to={period[1]} onChange={(a, b) => setPeriod([a, b])} /></div>}
      </div>
      {isReport ? <Report key={sub} name={sub} from={period[0]} to={period[1]} /> : sub === 'audit' ? <Audit /> : <Access />}
    </div>
  );
}
